import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Typography, Box } from '@mui/material';

interface EditableMarkdownTextProps {
  content: string;
  onSave: (newContent: string) => void;
  isEditable: boolean;
  wrapper?: React.ElementType; // Renamed from component to avoid conflict with MUI component prop
  typographyVariant?: any; // Variant can be string or undefined
  sx?: any;
  [key: string]: any; // Pass through other props
}

const EditableMarkdownText: React.FC<EditableMarkdownTextProps> = ({
  content,
  onSave,
  isEditable,
  wrapper: Component = Typography,
  typographyVariant,
  sx,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(content);
  const inputRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setText(content);
  }, [content]);

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    setIsEditing(false);
    const newText = e.currentTarget.innerText;
    // Only save if text actually changed
    // Use timeout to allow other events (like click on save button if one existed) to process
    // and to avoid conflicts with component unmounting
    if (newText !== content) {
      onSave(newText);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      // Ctrl+Enter or Cmd+Enter to save and exit
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setText(content); // Revert
      setIsEditing(false);
    }
    // Allow Enter to create new lines normally
  };

  const handleClick = (e: React.MouseEvent) => {
    if (isEditable) {
      e.stopPropagation(); // Prevent selecting the parent component
      setIsEditing(true);
    }
  };

  // Focus the element when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  if (isEditable && isEditing) {
    return (
      <Component
        ref={inputRef}
        variant={typographyVariant}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        sx={{
          ...sx,
          outline: '1px dashed #ccc',
          minHeight: '1em',
          cursor: 'text',
          whiteSpace: 'pre-wrap', // Preserve whitespace for markdown editing
        }}
        {...props}
      >
        {text}
      </Component>
    );
  }

  // Render Markdown (HTML)
  return (
    <Component
      onClick={handleClick}
      variant={typographyVariant}
      sx={{
        ...sx,
        cursor: isEditable ? 'text' : 'inherit',
        minHeight: '1em',
        // Add a visual cue that it's editable on hover
        '&:hover': isEditable
          ? {
              outline: '1px dashed #ccc',
            }
          : {},
      }}
      {...props}
    >
      <ReactMarkdown
        components={{
          p: ({ children }) => <>{children}</>, // Strip p tags to avoid nesting
          ul: ({ children }) => (
            <ul
              style={{
                marginTop: 0,
                paddingTop: 0,
                paddingLeft: '1.5em',
                listStylePosition: 'inside',
              }}
            >
              {children}
            </ul>
          ),
          li: ({ children }) => <li style={{ marginTop: 0 }}>{children}</li>,
          a: ({ href, children }) => (
            <a href={href} onClick={(e) => isEditable && e.preventDefault()}>
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </Component>
  );
};

export default EditableMarkdownText;
